import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    question: "What services do you offer?",
    answer: (
      <>
        We offer{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          website development
        </Link>
        ,{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          SEO services
        </Link>
        ,{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          social media marketing
        </Link>
        ,{" "}
        <Link
          to="/services/custom-tool-creation"
          className="text-primary hover:underline"
        >
          custom tool creation
        </Link>
        , and{" "}
        <Link
          to="/services/maintenance-support"
          className="text-primary hover:underline"
        >
          maintenance & support
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "We offer website development, SEO services, social media marketing, custom tool creation, and maintenance & support.",
  },
  {
    question: "Do you only work in Vancouver?",
    answer:
      "We are based in Vancouver, but we work with businesses across Canada.",
  },
  {
    question: "How long does a website take?",
    answer:
      "Most business websites take around 4 to 8 weeks, depending on scope and functionality.",
  },
  {
    question: "Is SEO included in a website build?",
    answer:
      "Yes. SEO foundations, structure, and performance are built into the website from the start.",
  },
  {
    question: "Can you improve an existing website?",
    answer:
      "Yes. We can improve structure, performance, SEO, and conversion without rebuilding everything.",
  },
  {
    question: "What industries do you work with?",
    answer:
      "We have strong experience in insurance and automotive, and we work with businesses that need better visibility, trust, and digital systems.",
  },
];

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleIndex = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="relative overflow-hidden bg-muted/30 py-12 sm:py-14 lg:py-16">
      {/* Background Decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-muted/30 to-transparent" />
      </div>

      <div className="container-custom relative z-10">
        <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8 text-center sm:mb-12"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Frequently Asked Questions
            </span>
            <h2 className="mt-3 mb-4 text-3xl font-display font-bold text-foreground md:text-4xl lg:text-5xl sm:mb-6">
              What Businesses Usually Ask{" "}
              <span className="gradient-text">First</span>
            </h2>
          </motion.div>

          {/* FAQ List */}
          <div className="mx-auto max-w-[1000px] space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.04 }}
                className="overflow-hidden rounded-xl border border-border bg-background"
              >
                <button
                  type="button"
                  onClick={() => toggleIndex(index)}
                  className="flex w-full items-start justify-between gap-4 p-4 text-left transition-colors hover:bg-accent/50 sm:items-center sm:p-6"
                >
                  <span className="leading-snug font-semibold text-foreground">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 sm:mt-0 ${
                      openIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 text-sm leading-relaxed text-muted-foreground sm:px-6 sm:pb-6 sm:text-base">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* FAQ Schema */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    typeof faq.answer === "string" ? faq.answer : faq.schemaAnswer,
                },
              })),
            })}
          </script>
        </div>
      </div>
    </section>
  );
};